import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  FileText,
  Flag,
  HandCoins,
  Users,
  Plus,
  Pencil,
  Trash2,
  LogOut,
  ExternalLink,
  X,
  Save,
} from "lucide-react";

/* ---------------- shared UI ---------------- */
const inputCls =
  "w-full rounded-sm border border-hairline bg-navy px-3 py-2.5 text-sm text-bone outline-none transition-colors duration-300 focus:border-kinetic";

function Field({ label, type = "text", value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-widest text-bone/45">{label}</span>
      {type === "textarea" ? (
        <textarea rows={4} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div
      data-lenis-prevent
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      data-testid="cms-modal"
    >
      <div className="mt-16 w-full max-w-lg rounded-sm border border-hairline bg-ink shadow-2xl">
        <div className="flex items-center justify-between border-b border-hairline px-6 py-4">
          <h3 className="font-display text-xl font-bold text-bone">{title}</h3>
          <button onClick={onClose} className="text-bone/50 hover:text-bone" data-testid="modal-close"><X size={20} /></button>
        </div>
        <div data-lenis-prevent className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

const btnPrimary =
  "inline-flex items-center gap-2 rounded-full bg-kinetic px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-transform duration-300 hover:-translate-y-0.5";
const btnGhost =
  "inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-xs uppercase tracking-wider text-bone/70 transition-colors duration-300 hover:border-bone/60 hover:text-bone";

/* ---------------- generic entity manager ---------------- */
function EntityManager({ title, singular, base, fields, primaryKey, secondaryKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(base.replace("/admin/", "/content/"));
    setItems(data);
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    const blank = {};
    fields.forEach((f) => (blank[f.name] = f.type === "number" ? items.length + 1 : ""));
    setEditing(blank);
    setIsNew(true);
  };

  const save = async () => {
    const payload = { ...editing };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.name] = parseInt(payload[f.name], 10) || 0;
    });
    try {
      if (isNew) await api.post(base, payload);
      else await api.put(`${base}/${editing.id}`, payload);
      toast.success(`${title} saved`);
      setEditing(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`${base}/${id}`);
    toast.success("Deleted");
    await load();
    onChanged && onChanged();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl font-bold text-bone">{title}</h2>
        <button className={btnPrimary} onClick={openNew} data-testid={`add-${title.toLowerCase()}`}>
          <Plus size={15} /> Add
        </button>
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.id}
            className="flex items-center justify-between gap-4 rounded-sm border border-hairline/70 bg-ink px-5 py-4"
            data-testid={`row-${title.toLowerCase()}`}
          >
            <div className="min-w-0">
              <div className="truncate font-medium text-bone">{it[primaryKey] || "Untitled"}</div>
              {secondaryKey && <div className="truncate text-sm text-bone/45">{it[secondaryKey]}</div>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button className={btnGhost} onClick={() => { setEditing({ ...it }); setIsNew(false); }} data-testid="edit-item">
                <Pencil size={13} /> Edit
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-full border border-kinetic/40 px-4 py-2 text-xs uppercase tracking-wider text-kinetic transition-colors duration-300 hover:bg-kinetic/10"
                onClick={() => remove(it.id)}
                data-testid="delete-item"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="rounded-sm border border-dashed border-hairline p-10 text-center text-bone/40">No items yet.</div>}
      </div>

      {editing && (
        <Modal title={`${isNew ? "New" : "Edit"} ${singular}`} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            {fields.map((f) => (
              <Field
                key={f.name}
                label={f.label}
                type={f.type}
                value={editing[f.name]}
                onChange={(v) => setEditing((p) => ({ ...p, [f.name]: v }))}
              />
            ))}
            <button className={`${btnPrimary} w-full justify-center`} onClick={save} data-testid="save-item">
              <Save size={15} /> Save
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- settings ---------------- */
function SettingsForm({ onChanged }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/content/settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="text-bone/40">Loading…</div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setSocial = (i, key, val) => {
    const links = [...(s.social_links || [])];
    links[i] = { ...links[i], [key]: val };
    set("social_links", links);
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl font-bold text-bone">Site Settings</h2>
        <button className={btnPrimary} onClick={save} data-testid="save-settings"><Save size={15} /> Save</button>
      </div>

      <div className="space-y-5">
        <Field label="Candidate Name" value={s.candidate_name} onChange={(v) => set("candidate_name", v)} />
        <Field label="Hero Overline" value={s.hero_overline} onChange={(v) => set("hero_overline", v)} />
        <Field label="Hero Heading (use line breaks)" type="textarea" value={s.hero_heading} onChange={(v) => set("hero_heading", v)} />
        <Field label="Hero Subheading" type="textarea" value={s.hero_subheading} onChange={(v) => set("hero_subheading", v)} />
        <Field label="Hero CTA Label" value={s.hero_cta_label} onChange={(v) => set("hero_cta_label", v)} />
        <Field label="Tagline" value={s.tagline} onChange={(v) => set("tagline", v)} />
        <Field label="Candidate Photo URL (Home 'Meet Mark' section)" value={s.candidate_photo} onChange={(v) => set("candidate_photo", v)} />
        <Field
          label="Marquee Items (one per line)"
          type="textarea"
          value={(s.marquee_items || []).join("\n")}
          onChange={(v) => set("marquee_items", v.split("\n").map((x) => x.trim()).filter(Boolean))}
        />
        <Field label="Candidate Bio" type="textarea" value={s.bio} onChange={(v) => set("bio", v)} />
        <Field label="Policy Page Intro" type="textarea" value={s.policy_intro} onChange={(v) => set("policy_intro", v)} />
        <Field label="Beliefs Page Intro" type="textarea" value={s.beliefs_intro} onChange={(v) => set("beliefs_intro", v)} />
        <Field label="Donate Page Intro" type="textarea" value={s.donate_intro} onChange={(v) => set("donate_intro", v)} />
        <Field label="Footer Email" value={s.footer_email} onChange={(v) => set("footer_email", v)} />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-bone/45">Social Links</span>
            <button className={btnGhost} onClick={() => set("social_links", [...(s.social_links || []), { label: "", url: "" }])}>
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-2">
            {(s.social_links || []).map((l, i) => (
              <div key={i} className="flex gap-2">
                <input className={inputCls} placeholder="Label" value={l.label} onChange={(e) => setSocial(i, "label", e.target.value)} />
                <input className={inputCls} placeholder="URL" value={l.url} onChange={(e) => setSocial(i, "url", e.target.value)} />
                <button
                  className="shrink-0 rounded-sm border border-kinetic/40 px-3 text-kinetic"
                  onClick={() => set("social_links", s.social_links.filter((_, j) => j !== i))}
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- team ---------------- */
function TeamManager() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/admin/users", adding);
      toast.success("Team member added");
      setAdding(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl font-bold text-bone">Team</h2>
        <button className={btnPrimary} onClick={() => setAdding({ name: "", email: "", password: "", role: "editor" })} data-testid="add-team">
          <Plus size={15} /> Add Member
        </button>
      </div>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-sm border border-hairline/70 bg-ink px-5 py-4">
            <div>
              <div className="font-medium text-bone">{u.name} <span className="ml-2 rounded-full bg-steel px-2 py-0.5 text-[10px] uppercase tracking-wider text-bone/60">{u.role}</span></div>
              <div className="text-sm text-bone/45">{u.email}</div>
            </div>
            {u.id !== user.id && (
              <button className="rounded-full border border-kinetic/40 px-4 py-2 text-xs uppercase tracking-wider text-kinetic hover:bg-kinetic/10" onClick={() => remove(u.id)}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <Modal title="New Team Member" onClose={() => setAdding(null)}>
          <div className="space-y-4">
            <Field label="Name" value={adding.name} onChange={(v) => setAdding((p) => ({ ...p, name: v }))} />
            <Field label="Email" value={adding.email} onChange={(v) => setAdding((p) => ({ ...p, email: v }))} />
            <Field label="Password" type="password" value={adding.password} onChange={(v) => setAdding((p) => ({ ...p, password: v }))} />
            <Field label="Role (admin / editor)" value={adding.role} onChange={(v) => setAdding((p) => ({ ...p, role: v }))} />
            <button className={`${btnPrimary} w-full justify-center`} onClick={save} data-testid="save-team"><Save size={15} /> Create</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- dashboard shell ---------------- */
const NAV = [
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "policies", label: "Policy", icon: FileText },
  { key: "beliefs", label: "Beliefs", icon: Flag },
  { key: "donations", label: "Donations", icon: HandCoins },
  { key: "team", label: "Team", icon: Users },
];

export default function Dashboard() {
  const [tab, setTab] = useState("settings");
  const { user, logout } = useAuth();
  const { refetch } = useContent();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="grain flex min-h-screen bg-navy" data-testid="dashboard">
      {/* sidebar */}
      <aside className="fixed left-0 top-0 hidden h-screen w-64 flex-col border-r border-hairline/70 bg-ink p-6 md:flex">
        <div className="font-display text-lg font-bold tracking-tight text-bone">
          PRASCAK <span className="text-kinetic">4</span> PRES
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-bone/40">Content Studio</div>

        <nav className="mt-10 flex flex-1 flex-col gap-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.key}
                onClick={() => setTab(n.key)}
                data-testid={`tab-${n.key}`}
                className={`flex items-center gap-3 rounded-sm px-4 py-3 text-sm transition-colors duration-300 ${
                  tab === n.key ? "bg-kinetic text-white" : "text-bone/60 hover:bg-navy hover:text-bone"
                }`}
              >
                <Icon size={16} /> {n.label}
              </button>
            );
          })}
        </nav>

        <div className="mt-auto space-y-2 border-t border-hairline/60 pt-4">
          <Link to="/" target="_blank" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider text-bone/50 hover:text-bone">
            <ExternalLink size={14} /> View Site
          </Link>
          <div className="px-4 text-xs text-bone/40">{user?.email}</div>
          <button onClick={doLogout} className="flex w-full items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider text-kinetic hover:text-[#f0554a]" data-testid="logout-btn">
            <LogOut size={14} /> Log Out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="fixed left-0 right-0 top-0 z-40 flex items-center gap-2 overflow-x-auto border-b border-hairline/70 bg-ink px-4 py-3 md:hidden">
        {NAV.map((n) => (
          <button key={n.key} onClick={() => setTab(n.key)} className={`whitespace-nowrap rounded-full px-4 py-2 text-xs ${tab === n.key ? "bg-kinetic text-white" : "text-bone/60"}`}>
            {n.label}
          </button>
        ))}
        <button onClick={doLogout} className="ml-auto whitespace-nowrap rounded-full px-3 py-2 text-xs text-kinetic">Exit</button>
      </div>

      {/* content */}
      <main className="flex-1 px-5 pb-24 pt-24 md:ml-64 md:px-12 md:pt-14">
        <div className="mx-auto max-w-3xl">
          {tab === "settings" && <SettingsForm onChanged={refetch} />}
          {tab === "policies" && (
            <EntityManager
              title="Policies"
              singular="Policy"
              base="/admin/policies"
              primaryKey="title"
              secondaryKey="summary"
              onChanged={refetch}
              fields={[
                { name: "title", label: "Title" },
                { name: "summary", label: "Summary" },
                { name: "body", label: "Blurb (shown on Policy page)", type: "textarea" },
                { name: "detail", label: "Full Text — 'Read more' page (line breaks supported)", type: "textarea" },
                { name: "order", label: "Order", type: "number" },
              ]}
            />
          )}
          {tab === "beliefs" && (
            <EntityManager
              title="Beliefs"
              singular="Belief"
              base="/admin/beliefs"
              primaryKey="title"
              secondaryKey="body"
              onChanged={refetch}
              fields={[
                { name: "number", label: "Number (e.g. 01)" },
                { name: "title", label: "Title" },
                { name: "body", label: "Blurb (shown on Beliefs page)", type: "textarea" },
                { name: "detail", label: "Full Text — 'Read more' page (line breaks supported)", type: "textarea" },
                { name: "order", label: "Order", type: "number" },
              ]}
            />
          )}
          {tab === "donations" && (
            <EntityManager
              title="Donations"
              singular="Donation"
              base="/admin/donations"
              primaryKey="method"
              secondaryKey="handle"
              onChanged={refetch}
              fields={[
                { name: "method", label: "Method (Venmo, PayPal…)" },
                { name: "handle", label: "Handle" },
                { name: "url", label: "Payment URL" },
                { name: "qr_image_url", label: "QR Image URL" },
                { name: "note", label: "Note", type: "textarea" },
                { name: "order", label: "Order", type: "number" },
              ]}
            />
          )}
          {tab === "team" && <TeamManager />}
        </div>
      </main>
    </div>
  );
}
